import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  BarChart3, Activity, DollarSign, Zap, Clock, Download, Loader2, Key,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsageDashboardTab() {
  const [usage, setUsage] = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when period changes; fetchUsage reads current period
  }, [period]);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/usage/summary?period=${period}`);
      setUsage(res.data);
    } catch {}
    finally { setLoading(false); }
  };

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/usage/export?format=${format}`);
      if (format === "csv") {
        const blob = new Blob([res.data.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `qnneal-usage-${period}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        navigator.clipboard.writeText(JSON.stringify(res.data.data, null, 2));
        toast.success("Usage data copied to clipboard");
      }
    } catch {
      toast.error("Failed to export usage data");
    }
  };

  const maxReq = Math.max(...(usage?.daily?.map(d => d.requests) || [1]));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--qn-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="usage-dashboard-tab">
      {/* Period Selector + Export */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2" data-testid="period-selector">
          {["week", "month", "year"].map(p => (
            <Button key={p} variant={period === p ? "default" : "outline"} size="sm"
              className={period === p ? "bg-[var(--qn-primary)] text-white" : "border-[var(--qn-border)]"}
              onClick={() => setPeriod(p)} data-testid={`period-${p}`}
            >
              {p === "week" ? "7 Days" : p === "month" ? "This Month" : "This Year"}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport("csv")} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("json")} data-testid="export-json-btn">
            <Download className="w-4 h-4 mr-2" /> JSON
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Requests", value: usage?.total_requests?.toLocaleString() || "0", icon: Activity, color: "text-[var(--qn-primary)]" },
          { label: "Total Tokens", value: usage?.total_tokens?.toLocaleString() || "0", icon: Zap, color: "text-[var(--qn-secondary)]" },
          { label: "Total Cost", value: `$${usage?.total_cost_usd?.toFixed(2) || "0.00"}`, icon: DollarSign, color: "text-emerald-400" },
          { label: "Active Keys", value: usage?.key_breakdown?.length?.toString() || "0", icon: Key, color: "text-amber-400" },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-2">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div className="text-2xl font-bold">{s.value}</div>
                <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">{s.label}</div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Daily Usage Chart */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-[var(--qn-primary)]" />
            Daily Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {usage?.daily?.length > 0 ? (
            <div className="flex items-end gap-1 h-40" data-testid="daily-usage-chart">
              {usage.daily.slice(-30).map((d, i) => {
                const height = maxReq > 0 ? Math.max(4, (d.requests / maxReq) * 100) : 4;
                return (
                  <div key={d.date} className="flex-1 group relative" title={`${d.date}: ${d.requests} requests`}>
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${height}%` }}
                      transition={{ delay: i * 0.01, duration: 0.3 }}
                      className="bg-[var(--qn-primary)] rounded-t-sm hover:bg-[var(--qn-primary)]/80 transition-colors w-full"
                    />
                    <div className="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-[var(--qn-card)] border border-[var(--qn-border)] rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                      {d.date}: {d.requests} req
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-sm text-[var(--qn-foreground-muted)]">No usage data for this period.</div>
          )}
        </CardContent>
      </Card>

      {/* Model Breakdown */}
      <div className="grid grid-cols-2 gap-6">
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader>
            <CardTitle className="font-outfit text-base">By Model</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4" data-testid="model-breakdown">
              {(usage?.model_breakdown || []).map((m) => (
                <div key={m.model}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{m.model}</span>
                    <span className="text-xs text-[var(--qn-foreground-muted)]">{m.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-[var(--qn-background)] rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${m.pct}%` }} transition={{ duration: 0.5 }} className="h-full bg-[var(--qn-primary)] rounded-full" />
                  </div>
                  <div className="flex justify-between text-xs text-[var(--qn-foreground-muted)] mt-1">
                    <span>{m.requests.toLocaleString()} requests</span>
                    <span>${m.cost_usd.toFixed(2)}</span>
                  </div>
                </div>
              ))}
              {(!usage?.model_breakdown || usage.model_breakdown.length === 0) && (
                <div className="text-center py-6 text-sm text-[var(--qn-foreground-muted)]">No model usage data.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader>
            <CardTitle className="font-outfit text-base">By API Key</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="key-breakdown">
              {(usage?.key_breakdown || []).map((k) => (
                <div key={k.key_preview} className="flex items-center gap-3 bg-[var(--qn-background)] rounded-lg p-3 border border-[var(--qn-border)]">
                  <Key className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{k.name}</div>
                    <div className="text-xs text-[var(--qn-foreground-muted)] font-mono">{k.key_preview}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{k.requests.toLocaleString()}</div>
                    <div className="text-xs text-[var(--qn-foreground-muted)]">${k.cost_usd.toFixed(2)}</div>
                  </div>
                </div>
              ))}
              {(!usage?.key_breakdown || usage.key_breakdown.length === 0) && (
                <div className="text-center py-6 text-sm text-[var(--qn-foreground-muted)]">No API key usage yet. Create a key and make some requests!</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
